import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { MessageType } from 'src/portaljs/constants/message-type';
import { MenuItem } from 'src/portaljs/models/menu-item';
import { Message } from 'src/portaljs/models/message';
import AuthType from '../enums/auth.type.enum';
import LoginPrompt from '../enums/login-prompt.enum';
import { AuthenticationEvent } from '../models/authentication-event.model';
import { AuthService } from './auth.service';
import { PortalStateService } from './portal-state.service';
import { PortalApp } from '../shared/portal-app';
import { SidenavMenuStates } from '../enums/sidenav-menu-states.enum';
import { Portal } from '@angular/cdk/portal';
import { ScreenStateService } from './screen-state.service';

interface SideNavPortalItem {
  templatePortal: Portal<any>,
  title: string
}

@Injectable({ providedIn: 'root' })
export class ToolbarService implements OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  public selectedPath: string[] = [];
  public portal: SideNavPortalItem;
  private _mobileMenuOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get isMobileMenuOpen(): boolean {
    return this.getShowHambugerMenu() && this._mobileMenuOpen.value;
  }

  public set isMobileMenuOpen(value: boolean) {
    this._mobileMenuOpen.next(value);
  }
  
  public get menuItems(): MenuItem[] {
    return this.portalStateService.activeMenuItems;
  }
  
  public isMobile() {
    return this.screenStateService.isMobile();
  }

  public isTablet() {
    return this.screenStateService.isTablet();
  }

  public isDesktop() {
    return this.screenStateService.isDesktop();
  }

  constructor(
    private readonly authService: AuthService,
    private readonly portalStateService: PortalStateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly screenStateService: ScreenStateService
  ) {
  }

  public isHighlighted(id: string): boolean {
    if (this.selectedPath) {
      return this.selectedPath.includes(id);
    }
    return false;
  }

  public toggleSidenav() {
    const menuState = this.portalStateService.getMenuStateForApp();
    if (this.isDesktop() || this.isTablet()) {
      if (menuState === SidenavMenuStates.STANDARD) {
        this.portalStateService.setMenuStateForApp(SidenavMenuStates.SMALL_ICONS);
      } else {
        this.portalStateService.setMenuStateForApp(SidenavMenuStates.STANDARD);
      }
    } else {
      if (menuState === SidenavMenuStates.STANDARD) {
        this.portalStateService.setMenuStateForApp(SidenavMenuStates.HIDDEN);
      } else {
        this.portalStateService.setMenuStateForApp(SidenavMenuStates.STANDARD);
      }
    }
  }

  public getShowHambugerMenu(): boolean {
    return (
      this.isMobile() ||
      (this.isTablet() &&
      (this.getIsAppMenuEnabled() || this.portalStateService.getIsDestinyViewSelectorEnabled()))
    );
  }

  private getIsAppMenuEnabled(): boolean {
    return this.portalStateService.activeMenuItems.length > 0;
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Close the sidenav menu when another menu gets open.
   *
   * Currently, others menu are being shown outside of the sidenav itself only on tablet view,
   * so we need to close this only for tablet.
   */
  public closeSidenavOnMenuOpen() {
    if (this.isTablet()) {
      this.isMobileMenuOpen = false;
    }
  }

  public login() {
    this.isMobileMenuOpen = false;
    this.portal = undefined;
    this.authService.initiateAuthentication(
      new AuthenticationEvent(LoginPrompt.LOGIN, AuthType.LOGIN, this.authService.getSiteGuid(), 'toolbar-login')
    );
  }

  public menuSelected(menu: MenuItem) {
    const activeApp: PortalApp = this.portalStateService.getActiveApp();
    const msg = new Message(
      menu.id,
      MessageType.menuItemSelect,
      Application.PORTAL,
      activeApp.app,
      'clicked'
    );
    this.portalStateService.postMessage(msg);
    this.isMobileMenuOpen = false;

    if (menu.externalUrl) {
      window.open(menu.externalUrl, '_blank');
    }
  }

  public getTopLevelMenuId(subMenuItemId: string, menuItems: MenuItem[] = this.portalStateService.activeMenuItems): string {
    for (const x of menuItems) {
      if (x.id === subMenuItemId) {
        return x.id;
      } else if (x.subMenuItems) {
        if (this.getTopLevelMenuId(subMenuItemId, x.subMenuItems)) {
          return x.id;
        }
      }
    }
    return null;
  }

  public getSelectedMenuFromPath() {
    return this.selectedPath[this.selectedPath.length - 1];
  }
}
